import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import PageHead from "../../components/PageHead";
import * as api from "./api";
import OfferPicker from "./OfferPicker";
import { Async, Kpi, KpiRow, Metric, Panel } from "./panels";
import { useEndpoint } from "./useOffer";

/**
 * Workstation · Analytics (§4.2).
 *
 * THE OVERALL / INDIVIDUAL SWITCH
 * ===============================
 * `workstation.md` splits this tab in two: Overall is the offer's performance
 * in aggregate; Individual is per-ad and per-creative, with lineage on select.
 * The switch is in the URL (`?view=`) for the same reason the offer is — a view
 * somebody was looking at should survive being described to somebody else.
 *
 * OVERALL AND INDIVIDUAL ARE BOTH FULLY BACKED (R4 CLOSED)
 * =========================================================
 *   Overall      snapshots, P&L, predictions, campaign health — every function
 *                has a live offer-scoped endpoint.
 *   Individual   creative-grain engagement is backed by
 *                `/api/performance/creative-engagement`, and AD-GRAIN
 *                performance is backed by
 *                `/api/offers/{offer_id}/ads/performance`
 *                (`backend/routers/performance.py:1273`), which merges
 *                `ad_performance_snapshots` (Meta) and
 *                `redtrack_ad_performance_snapshots` (RedTrack) and was built
 *                specifically for this panel.
 *
 * The per-ad rankings `workstation.md` asks for — Top/Worst Performers, Most
 * Profitable, Highest CTR, Lowest CPC — read from that endpoint now rather
 * than being approximated from the coarser creative-grain source, which would
 * have produced a ranking that looks right, is wrong, and says nothing on
 * screen about the difference.
 *
 * FOUNDER-GATED PANELS ARE NOT FAILURES
 * =====================================
 * `/api/decisions/quality-summary` is `require_founder`. A non-founder gets a
 * 403 and a rendered refusal that says the gate is working. R5: the navigation
 * agrees with the backend, and the backend is what enforces.
 */

const OVERALL = "overall";
const INDIVIDUAL = "individual";

/** The switch. Seeded from `?view=`, held in state — same contract reasoning
 *  as the offer selection: an area uses only what the shell provides, and
 *  `useLocation` is that surface. See `useOffer.js`. */
function useView() {
  const location = useLocation();
  let initial = OVERALL;
  try {
    if (new URLSearchParams(location?.search || "").get("view") === INDIVIDUAL) {
      initial = INDIVIDUAL;
    }
  } catch (e) {
    /* a location without a usable search is simply the default view */
  }
  return useState(initial);
}

function ViewSwitch({ view, setView }) {
  return (
    <div className="seg ws-view-switch" role="tablist" aria-label="Analytics view">
      {[
        [OVERALL, "Overall"],
        [INDIVIDUAL, "Individual"],
      ].map(([id, label]) => (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={view === id}
          className={view === id ? "ws-seg-btn on" : "ws-seg-btn"}
          onClick={() => setView(id)}
          data-testid={`ws-analytics-${id}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function rollUp(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const sum = (k) => rows.reduce((a, r) => a + (Number(r?.[k]) || 0), 0);
  const spend = sum("spend");
  const revenue = sum("revenue");
  const conversions = sum("conversions");
  return {
    spend,
    revenue,
    profit: revenue - spend,
    roas: spend > 0 ? revenue / spend : null,
    cpa: conversions > 0 ? spend / conversions : null,
    conversions,
  };
}

function Overall({ offerId }) {
  const snapshots = useEndpoint(() => api.getSnapshots(offerId), [offerId], {
    skip: !offerId,
  });
  const pnl = useEndpoint(() => api.getPnl(offerId), [offerId], {
    skip: !offerId,
  });
  const predictions = useEndpoint(() => api.getPredictionsSummary(), []);
  const atRisk = useEndpoint(() => api.getAtRisk(offerId), [offerId], {
    skip: !offerId,
  });
  const scale = useEndpoint(() => api.getScaleCandidates(offerId), [offerId], {
    skip: !offerId,
  });
  const quality = useEndpoint(() => api.getDecisionQuality(), []);

  const kpi = useMemo(
    () => (snapshots.state === "ready" ? rollUp(snapshots.data) : null),
    [snapshots.state, snapshots.data]
  );

  return (
    <div className="ws-analytics-grid">
      <Panel
        title="This offer, in aggregate"
        subtitle="Summed from the snapshots its campaigns wrote."
      >
        <Async
          result={snapshots}
          empty="No snapshots for this offer in the window."
        >
          {() =>
            kpi === null ? (
              <div className="state ws-state">No snapshots for this offer.</div>
            ) : (
              <KpiRow>
                <Kpi label="Spend" value={kpi.spend} format="currency" />
                <Kpi label="Revenue" value={kpi.revenue} format="currency" />
                <Kpi label="Profit" value={kpi.profit} format="currency" />
                <Kpi
                  label="ROAS"
                  value={kpi.roas}
                  format="ratio"
                  note={kpi.roas === null ? "undefined — no spend" : null}
                />
                <Kpi
                  label="CPA"
                  value={kpi.cpa}
                  format="currency"
                  note={kpi.cpa === null ? "undefined — no conversions" : null}
                />
              </KpiRow>
            )
          }
        </Async>
      </Panel>

      <Panel title="P&amp;L" subtitle="Offer-scoped records, as the ledger has them.">
        <Async
          result={pnl}
          empty="No P&L records for this offer yet."
        >
          {(data) => {
            const rows = Array.isArray(data) ? data : data?.records || [];
            if (rows.length === 0) {
              return (
                <div className="state ws-state">No P&amp;L records for this offer.</div>
              );
            }
            return (
              <div className="ws-table-scroll">
                <table className="tbl ws-table">
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th>Revenue</th>
                      <th>Spend</th>
                      <th>Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 12).map((r, i) => (
                      <tr key={r.pnl_id || r._id || i}>
                        <td>{r.period || r.date || "—"}</td>
                        <td><Metric value={r.revenue} format="currency" /></td>
                        <td><Metric value={r.spend} format="currency" /></td>
                        <td><Metric value={r.profit} format="currency" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }}
        </Async>
      </Panel>

      <Panel
        title="Campaign health"
        subtitle="At risk and worth scaling, as the health analysis flagged them."
      >
        <div className="ws-two-up">
          <div>
            <h3 className="section-title">At risk</h3>
            <Async
              result={atRisk}
              empty="Nothing flagged at risk. The health analysis is founder-triggered and may simply not have run."
            >
              {(data) => (
                <ul className="ws-list">
                  {(Array.isArray(data) ? data : data?.campaigns || [])
                    .slice(0, 8)
                    .map((c, i) => (
                      <li key={c.campaign_id || i}>
                        {c.campaign_name || c.campaign_id}
                      </li>
                    ))}
                </ul>
              )}
            </Async>
          </div>
          <div>
            <h3 className="section-title">Scale candidates</h3>
            <Async
              result={scale}
              empty="Nothing flagged as a scale candidate."
            >
              {(data) => (
                <ul className="ws-list">
                  {(Array.isArray(data) ? data : data?.campaigns || [])
                    .slice(0, 8)
                    .map((c, i) => (
                      <li key={c.campaign_id || i}>
                        {c.campaign_name || c.campaign_id}
                      </li>
                    ))}
                </ul>
              )}
            </Async>
          </div>
        </div>
      </Panel>

      <Panel title="Predictions" subtitle="Across the account, from the prediction engine.">
        <Async result={predictions} empty="No predictions recorded yet.">
          {(data) => (
            <KpiRow>
              <Kpi label="Total" value={data?.total ?? data?.count ?? null} />
              <Kpi label="Active" value={data?.active ?? null} />
              <Kpi
                label="Accuracy"
                value={data?.accuracy ?? null}
                format="percent"
              />
            </KpiRow>
          )}
        </Async>
      </Panel>

      <Panel
        title="Decision quality"
        subtitle="Follow rates and override patterns. Founder-only, at the server."
      >
        <Async
          result={quality}
          empty="No decision history yet."
          deniedLabel="Decision quality is founder-only and the server refused this read. That is §3.1 working, not a fault."
        >
          {(data) => (
            <KpiRow>
              <Kpi
                label="Follow rate"
                value={data?.follow_rate ?? null}
                format="percent"
              />
              <Kpi label="Decisions" value={data?.total_decisions ?? null} />
              <Kpi label="Overrides" value={data?.overrides ?? null} />
            </KpiRow>
          )}
        </Async>
      </Panel>
    </div>
  );
}

function Individual({ offerId }) {
  const engagement = useEndpoint(() => api.getCreativeEngagement(), []);
  const adPerformance = useEndpoint(() => api.getAdPerformance(offerId), [offerId], {
    skip: !offerId,
  });

  return (
    <div className="ws-analytics-grid">
      <Panel
        title="Per-ad rankings"
        subtitle="Top performers by spend, from ad-grain Meta + RedTrack data. R4, closed — see `performance.py:1273`."
      >
        <Async result={adPerformance} empty="No ad-grain performance rows yet for this offer.">
          {(data) => {
            const rows = Array.isArray(data) ? data : data?.ads || [];
            if (rows.length === 0) {
              return <div className="state ws-state">No ad-grain performance rows.</div>;
            }
            return (
              <div className="ws-table-scroll">
                <table className="tbl ws-table">
                  <thead>
                    <tr>
                      <th>Ad</th>
                      <th>Impressions</th>
                      <th>Clicks</th>
                      <th>CTR</th>
                      <th>Thumb-stop</th>
                      <th>Spend</th>
                      <th>ROAS</th>
                      <th>Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 20).map((r, i) => (
                      <tr key={r.ad_id || r.ad_name || i}>
                        <td>{r.ad_name || r.ad_id || "—"}</td>
                        <td>{r.impressions ?? "—"}</td>
                        <td>{r.clicks ?? "—"}</td>
                        <td><Metric value={r.ctr ?? null} format="percent" /></td>
                        <td><Metric value={r.thumb_stop_rate ?? null} format="percent" /></td>
                        <td><Metric value={r.spend ?? null} format="currency" /></td>
                        <td><Metric value={r.roas ?? null} format="ratio" /></td>
                        <td><Metric value={r.profit ?? null} format="currency" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }}
        </Async>
      </Panel>

      <Panel
        title="Creative engagement"
        subtitle="CTR, CPM, watch time, thumb-stop and hook retention, joined to spend and ROAS."
      >
        <Async
          result={engagement}
          empty="No creative engagement rows yet."
        >
          {(data) => {
            const rows = Array.isArray(data) ? data : data?.creatives || [];
            if (rows.length === 0) {
              return (
                <div className="state ws-state">No creative engagement rows.</div>
              );
            }
            return (
              <div className="ws-table-scroll">
                <table className="tbl ws-table">
                  <thead>
                    <tr>
                      <th>Creative</th>
                      <th>CTR</th>
                      <th>CPM</th>
                      <th>Spend</th>
                      <th>ROAS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 20).map((r, i) => (
                      <tr key={r.creative_id || i}>
                        <td>{r.creative_id || r.creative_name || "—"}</td>
                        <td><Metric value={r.ctr ?? null} format="percent" /></td>
                        <td><Metric value={r.cpm ?? null} format="currency" /></td>
                        <td><Metric value={r.spend ?? null} format="currency" /></td>
                        <td><Metric value={r.roas ?? null} format="ratio" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }}
        </Async>
      </Panel>

      <Panel
        title="Creative lineage"
        subtitle="Psychology map → angle → hook → script → CTA → production → session."
      >
        <div className="state ws-state" role="status">
          <p className="dim">
            Every hop in the lineage chain exists as an endpoint and the audit
            lists all eight. Wiring it belongs with Step 6, which relocates the
            creative components into the Workstation; building half of it here
            would put the same chain in two places.
          </p>
        </div>
      </Panel>
    </div>
  );
}

export default function Analytics({ selection }) {
  const { offers, selected, selectedId, select, error } = selection;
  const [view, setView] = useView();

  return (
    <>
      <PageHead
        title="Analytics"
        subtitle={
          selected
            ? `How ${selected.name || "this offer"} is performing.`
            : "How this offer is performing."
        }
        actions={<ViewSwitch view={view} setView={setView} />}
      />

      <OfferPicker
        offers={offers}
        selectedId={selectedId}
        onSelect={select}
        error={error}
      />

      {!selectedId ? (
        <div className="state ws-state" role="status">
          Select an offer to see its analytics.
        </div>
      ) : view === INDIVIDUAL ? (
        <Individual offerId={selectedId} />
      ) : (
        <Overall offerId={selectedId} />
      )}
    </>
  );
}
